/** Google Consent Mode v2 gate. Analytics/ads storage default to denied until the user decides. */

const CONSENT_KEY = 'induce_consent';
const DEFAULT_GOOGLE_MEASUREMENT_ID = 'G-FXN787YSSB';

type AnalyticsEnv = { VITE_GA_MEASUREMENT_ID?: string };

/** Resolve the stream ID at build time while keeping a safe production fallback. */
export function resolveMeasurementId(env?: AnalyticsEnv): string {
  return env?.VITE_GA_MEASUREMENT_ID?.trim() || DEFAULT_GOOGLE_MEASUREMENT_ID;
}

const GOOGLE_MEASUREMENT_ID = resolveMeasurementId(
  (import.meta as ImportMeta & { env?: AnalyticsEnv }).env,
);
const GOOGLE_TAG_URL = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GOOGLE_MEASUREMENT_ID)}`;

export type ConsentStatus = 'granted' | 'denied';

export function getStoredConsent(): ConsentStatus | null {
  try {
    const value = localStorage.getItem(CONSENT_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch {
    return null;
  }
}

const listeners = new Set<(status: ConsentStatus) => void>();

type AnalyticsWindow = {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
};
type AnalyticsScript = {
  async?: boolean;
  src?: string;
  onload?: () => void;
  onerror?: () => void;
};
type AnalyticsDocument = {
  querySelector: <T = AnalyticsScript>(selector: string) => T | null;
  createElement: (tagName: string) => AnalyticsScript;
  head: {
    appendChild: (script: AnalyticsScript) => void;
    removeChild?: (script: AnalyticsScript) => void;
  };
};

let consentDefaultQueued = false;
let analyticsConfigured = false;
let analyticsScriptPromise: Promise<void> | null = null;

function getAnalyticsWindow(): AnalyticsWindow | null {
  const runtime = globalThis as typeof globalThis & { window?: AnalyticsWindow };
  return runtime.window ?? null;
}

function getAnalyticsDocument(): AnalyticsDocument | null {
  const runtime = globalThis as typeof globalThis & { document?: AnalyticsDocument };
  return runtime.document ?? null;
}

/** Keep the queue local until explicit consent permits loading Google's script. */
function ensureLocalGtag(): AnalyticsWindow | null {
  const analyticsWindow = getAnalyticsWindow();
  if (!analyticsWindow) return null;

  analyticsWindow.dataLayer = analyticsWindow.dataLayer || [];
  if (typeof analyticsWindow.gtag !== 'function') {
    // Google Tag expects command entries in the same `arguments` shape used by
    // its canonical snippet. Plain arrays are left in the data layer but are not
    // processed as gtag commands, so no page-view or event request is emitted.
    analyticsWindow.gtag = function gtag() {
      analyticsWindow.dataLayer?.push(arguments);
    };
  }

  if (!consentDefaultQueued) {
    analyticsWindow.gtag('consent', 'default', {
      ad_storage: 'denied',
      analytics_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      wait_for_update: 500,
    });
    consentDefaultQueued = true;
  }

  return analyticsWindow;
}

function consentUpdate(status: ConsentStatus): void {
  const analyticsWindow = ensureLocalGtag();
  if (!analyticsWindow?.gtag) return;

  analyticsWindow.gtag('consent', 'update', {
    // This site uses GA4 analytics only. Advertising storage/signals remain denied.
    ad_storage: 'denied',
    analytics_storage: status === 'granted' ? 'granted' : 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
}

function configureAnalytics(): void {
  if (analyticsConfigured) return;
  const analyticsWindow = ensureLocalGtag();
  if (!analyticsWindow?.gtag) return;

  analyticsWindow.gtag('js', new Date());
  analyticsWindow.gtag('config', GOOGLE_MEASUREMENT_ID, {
    linker: { domains: ['inducelabs.ai', 'studio.inducelabs.ai'] },
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
  analyticsConfigured = true;
}

/** Load gtag exactly once, and only after analytics consent has been granted. */
export function loadAnalyticsScript(): void {
  if (getStoredConsent() !== 'granted') return;
  if (analyticsScriptPromise) return;
  const analyticsDocument = getAnalyticsDocument();
  if (!analyticsDocument) return;

  const analyticsWindow = ensureLocalGtag();
  if (!analyticsWindow) return;

  // Queue the consent update and config before the script can make a request.
  consentUpdate('granted');
  configureAnalytics();

  const existingScript = analyticsDocument.querySelector<AnalyticsScript>(
    `script[src="${GOOGLE_TAG_URL}"]`,
  );
  if (existingScript) {
    analyticsScriptPromise = Promise.resolve();
    return;
  }

  analyticsScriptPromise = new Promise<void>((resolve) => {
    const script = analyticsDocument.createElement('script');
    script.async = true;
    script.src = GOOGLE_TAG_URL;
    script.onload = () => resolve();
    script.onerror = () => {
      // A transient CDN failure must not permanently disable later retries.
      analyticsScriptPromise = null;
      if (analyticsDocument.head.removeChild) {
        analyticsDocument.head.removeChild(script);
      }
      resolve();
    };
    analyticsDocument.head.appendChild(script);
  });
}

/**
 * Notified the moment a decision is made, so UI that must not coexist with the banner
 * (the preferred-source bar) can react without polling localStorage.
 */
export function subscribeConsent(listener: (status: ConsentStatus) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setStoredConsent(status: ConsentStatus): void {
  try {
    localStorage.setItem(CONSENT_KEY, status);
  } catch {
    // Fail-open
  }
  listeners.forEach((listener) => {
    try {
      listener(status);
    } catch {
      // A misbehaving subscriber must not break the consent write.
    }
  });
}

export function applyConsent(status: ConsentStatus): void {
  try {
    ensureLocalGtag();
    if (status === 'granted') {
      loadAnalyticsScript();
      return;
    }

    // A loaded script cannot be unloaded safely; revoke consent for future hits.
    consentUpdate('denied');
  } catch {
    // Fail-open
  }
}

/** Re-apply a previously stored decision on boot (consent otherwise defaults to denied). */
export function applyStoredConsent(): void {
  const status = getStoredConsent();
  if (status) {
    applyConsent(status);
  } else {
    ensureLocalGtag();
  }
}
